import React from 'react';
import { motion } from 'framer-motion';
import { useConfig } from '@context/ConfigContext';
import Carousel from '@components/shared/Carousel/Carousel';
import styles from './Testimonials.module.css';

const StarRating: React.FC<{ rating: number }> = ({ rating }) => {
  return (
    <div className={styles.stars}>
      {[1, 2, 3, 4, 5].map((star) => (
        <motion.i
          key={star}
          className={`fas fa-star ${star <= rating ? styles.starFilled : styles.starEmpty}`}
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: star * 0.1, duration: 0.3 }}
        />
      ))}
    </div>
  );
};

const Testimonials: React.FC = () => {
  const { config } = useConfig();

  if (!config || !config.components.testimonials.enabled) return null;

  const { testimonials } = config.components;

  if (!testimonials.items || testimonials.items.length === 0) {
    return null;
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut',
      },
    },
  };

  return (
    <section id="testimonials" className={styles.testimonials}>
      <div className={styles.container}>
        <motion.div
          className={styles.content}
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
        >
          {/* Section Header */}
          <motion.div className={styles.header} variants={itemVariants}>
            <h2 className={styles.title}>What Our Clients Say</h2>
            <div className={styles.divider}></div>
            <p className={styles.subtitle}>
              Don't just take our word for it - hear from our satisfied clients
            </p>
          </motion.div>

          {/* Testimonials Carousel */}
          <motion.div variants={itemVariants}>
            <Carousel
              autoplay={testimonials.carousel.autoplay}
              autoplaySpeed={testimonials.carousel.interval}
              dots
              arrows
              slidesToShow={testimonials.carousel.itemsPerView}
              pauseOnHover
              responsive={[
                {
                  breakpoint: 1024,
                  settings: {
                    slidesToShow: Math.min(testimonials.carousel.itemsPerView, 2),
                    slidesToScroll: 1,
                  },
                },
                {
                  breakpoint: 768,
                  settings: {
                    slidesToShow: testimonials.carousel.mobileItemsPerView,
                    slidesToScroll: 1,
                    arrows: false,
                  },
                },
              ]}
            >
              {testimonials.items.map((testimonial, index) => (
                <div key={index} className={styles.testimonialSlide}>
                  <div className={styles.testimonialCard}>
                    <div className={styles.quoteIcon}>
                      <i className="fas fa-quote-left"></i>
                    </div>

                    <p className={styles.testimonialText}>{testimonial.text}</p>

                    <StarRating rating={testimonial.rating} />

                    <div className={styles.testimonialAuthor}>
                      <div className={styles.authorInfo}>
                        <p className={styles.authorName}>{testimonial.name}</p>
                        {testimonial.company && (
                          <p className={styles.authorCompany}>{testimonial.company}</p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </Carousel>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Testimonials;